import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Volume2, Trash2, Bookmark, Plus, Repeat } from "lucide-react";
import MobileShell from "@/components/MobileShell";
import client, { formatApiError } from "@/lib/api";
import { playTTS } from "@/lib/audio";
import { useLang } from "@/context/LangContext";
import { localizeCategory } from "@/i18n";
import PronounceModal from "@/components/PronounceModal";

const CATEGORIES = ["All", "Work", "Shopping", "Health", "Travel", "Daily Life", "Custom"];
const CAT_COLOR = {
  Work: "bg-blue-500/15 text-blue-300", Shopping: "bg-violet-500/15 text-violet-300",
  Health: "bg-rose-500/15 text-rose-300", Travel: "bg-cyan-500/15 text-cyan-300",
  "Daily Life": "bg-fuchsia-500/15 text-fuchsia-300", Custom: "bg-amber-500/15 text-amber-300",
};

export default function MyPhrases() {
  const { t, lang } = useLang();
  const [phrases, setPhrases] = useState([]);
  const [filter, setFilter] = useState("All");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ target_text: "", translation: "", category: "Custom" });
  const [coach, setCoach] = useState(null);

  const load = () => client.get("/phrases").then(({ data }) => setPhrases(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => { await client.delete(`/phrases/${id}`); setPhrases((p) => p.filter((x) => x.id !== id)); toast.success(t("deleted_toast")); };
  const add = async (e) => {
    e.preventDefault(); if (!form.target_text.trim()) return;
    try { await client.post("/phrases", form); setForm({ target_text: "", translation: "", category: "Custom" }); setAdding(false); load(); toast.success(t("phrase_saved_toast")); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const list = filter === "All" ? phrases : phrases.filter((p) => p.category === filter);

  return (
    <MobileShell>
      <div className="px-5 py-6">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl font-extrabold text-white">{t("my_phrases")}</h1>
          <button onClick={() => setAdding((v) => !v)} data-testid="add-phrase-btn" className="w-10 h-10 rounded-full mel-gradient text-white flex items-center justify-center shadow-[0_10px_24px_-8px_rgba(37,99,235,0.8)] press"><Plus size={20} /></button>
        </div>
        <p className="text-slate-400 mt-1">{t("phrases_sub")}.</p>

        {adding && (
          <form onSubmit={add} className="mt-4 glass rounded-2xl p-4 space-y-3 animate-float-up">
            <input value={form.target_text} onChange={(e) => setForm({ ...form, target_text: e.target.value })} placeholder={t("phrase_target_ph")} data-testid="phrase-target" className="w-full bg-white/5 rounded-xl px-3 py-2.5 outline-none text-white placeholder:text-slate-500" />
            <input value={form.translation} onChange={(e) => setForm({ ...form, translation: e.target.value })} placeholder={t("translation_ph")} data-testid="phrase-translation" className="w-full bg-white/5 rounded-xl px-3 py-2.5 outline-none text-white placeholder:text-slate-500" />
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="phrase-category" className="w-full bg-white/5 rounded-xl px-3 py-2.5 outline-none text-white">
              {CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c} value={c} className="bg-mel-navy">{localizeCategory(lang, c)}</option>)}
            </select>
            <button type="submit" data-testid="phrase-save" className="w-full mel-gradient text-white font-semibold py-3 rounded-xl press">{t("save_phrase")}</button>
          </form>
        )}

        <div className="flex gap-2 overflow-x-auto no-scrollbar mt-5 pb-1">
          {CATEGORIES.map((c) => (
            <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c.replace(/\s+/g, "-").toLowerCase()}`}
              className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${filter === c ? "mel-gradient text-white" : "glass text-slate-300"}`}>{c === "All" ? t("all") : localizeCategory(lang, c)}</button>
          ))}
        </div>

        <div className="mt-4 space-y-3">
          {list.length === 0 && (
            <div className="text-center py-16 text-slate-500"><Bookmark className="mx-auto mb-3 opacity-40" size={36} /><p>{t("no_phrases")}</p></div>
          )}
          {list.map((p) => (
            <div key={p.id} className="glass rounded-2xl p-4" data-testid={`phrase-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${CAT_COLOR[p.category] || "bg-white/10 text-slate-300"}`}>{localizeCategory(lang, p.category)}</span>
                  <p className="font-semibold text-white mt-1.5">{p.target_text}</p>
                  <p className="text-sm text-slate-400">{p.translation}</p>
                </div>
                <button onClick={() => del(p.id)} data-testid={`phrase-delete-${p.id}`} className="text-slate-500 hover:text-red-400"><Trash2 size={18} /></button>
              </div>
              <div className="flex gap-3 mt-3">
                <button onClick={() => playTTS(p.target_text)} className="flex items-center gap-1 text-sm font-medium text-cyan-300"><Volume2 size={15} /> {t("listen")}</button>
                <button onClick={() => setCoach(p)} data-testid={`phrase-practice-${p.id}`} className="flex items-center gap-1 text-sm font-medium text-violet-300"><Repeat size={15} /> {t("nav_practice")}</button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {coach && <PronounceModal phrase={coach.target_text} translation={coach.translation} onClose={() => setCoach(null)} />}
    </MobileShell>
  );
}
